import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Star, MapPin, ExternalLink } from 'lucide-react';

interface HotelProperty {
  id: string;
  hotel_name: string;
  star_rating: number | null;
  distance_miles: number | null;
  group_rate_per_night: number | null;
  rate_valid_until: string | null;
  amenities: string[] | null;
  photo_url: string | null;
  is_primary: boolean | null;
  booking_link: string | null;
  rfp_status: string | null;
}

interface OfficialHotelsSectionProps {
  eventId: string;
}

const OfficialHotelsSection: React.FC<OfficialHotelsSectionProps> = ({ eventId }) => {
  const [hotels, setHotels] = useState<HotelProperty[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchHotels = async () => {
      try {
        const { data, error } = await (supabase as any)
          .from('tbd_hotel_properties')
          .select('id, hotel_name, star_rating, distance_miles, group_rate_per_night, rate_valid_until, amenities, photo_url, is_primary, booking_link, rfp_status')
          .eq('event_id', eventId)
          .or('rfp_status.eq.accepted,rfp_status.is.null')
          .order('is_primary', { ascending: false })
          .order('hotel_name');

        if (!error && data && data.length > 0) {
          setHotels(data);
        }
      } catch {
        // Table may not exist yet — gracefully return nothing
      }
      setLoading(false);
    };
    fetchHotels();
  }, [eventId]);

  if (loading || hotels.length === 0) return null;

  return (
    <div className="rounded-xl bg-white shadow p-6 mb-6">
      <h2 className="text-lg font-semibold mb-4">Official Hotels</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {hotels.map((hotel) => (
          <Card key={hotel.id} className="overflow-hidden">
            {hotel.photo_url && (
              <img
                src={hotel.photo_url}
                alt={hotel.hotel_name}
                className="w-full h-40 object-cover"
                onError={(e) => { e.currentTarget.style.display = 'none'; }}
              />
            )}
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <h3 className="font-semibold text-base">{hotel.hotel_name}</h3>
                {hotel.is_primary && (
                  <Badge className="shrink-0 border-transparent bg-yellow-500 text-white text-xs">Recommended</Badge>
                )}
              </div>

              {hotel.star_rating != null && hotel.star_rating > 0 && (
                <div className="flex items-center gap-1 text-yellow-500 text-sm">
                  {Array.from({ length: hotel.star_rating }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-current" />
                  ))}
                </div>
              )}

              {hotel.distance_miles != null && (
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5" />
                  {hotel.distance_miles} mi from venue
                </p>
              )}

              {hotel.group_rate_per_night != null && (
                <p className="text-sm font-medium">
                  ${hotel.group_rate_per_night.toFixed(2)}{' '}
                  <span className="text-muted-foreground font-normal">/ night</span>
                </p>
              )}

              {hotel.rate_valid_until && (
                <p className="text-xs text-muted-foreground">
                  Rate valid until {new Date(hotel.rate_valid_until).toLocaleDateString()}
                </p>
              )}

              {hotel.amenities && hotel.amenities.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  {hotel.amenities.map((a, i) => (
                    <Badge key={i} variant="outline" className="text-xs font-normal">{a}</Badge>
                  ))}
                </div>
              )}

              {hotel.booking_link && (
                <Button
                  size="sm"
                  className="w-full mt-2"
                  onClick={() => window.open(hotel.booking_link!, '_blank')}
                >
                  Book This Hotel <ExternalLink className="w-3.5 h-3.5 ml-1" />
                </Button>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default OfficialHotelsSection;
